import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Handler from "../../Helpers/Handler";

// Search-and-select customer picker for granting a targeted coupon. Reuses
// the existing /admin/customer/list/:page search endpoint rather than adding
// a new one - it already supports name/phone/email/customerId search.
const GrantModal = ({ couponCode, onClose, onGranted }) => {
    const modalRef = useRef(null);
    const bsModalRef = useRef(null);
    const [search, setSearch] = useState("");
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [selected, setSelected] = useState({}); // customerId -> { customerId, label }
    const [expiresAt, setExpiresAt] = useState("");
    const [granting, setGranting] = useState(false);

    useEffect(() => {
        const el = modalRef.current;
        if (!el || !window.bootstrap) return undefined;
        const instance = window.bootstrap.Modal.getOrCreateInstance(el);
        bsModalRef.current = instance;
        const handleHidden = () => onClose?.();
        el.addEventListener("hidden.bs.modal", handleHidden);
        instance.show();
        return () => { el.removeEventListener("hidden.bs.modal", handleHidden); instance.hide(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const runSearch = async () => {
        if (!search.trim()) { setResults([]); return; }
        setSearching(true);
        const response = await Handler({ method: "post", url: "/admin/customer/list/0", params: { count: 10 }, data: { search: search.trim() } });
        setResults(response?.success ? response?.data?.data?.list || [] : []);
        setSearching(false);
    };

    const toggleSelect = (customer) => {
        setSelected((prev) => {
            const next = { ...prev };
            if (next[customer.customerId]) delete next[customer.customerId];
            else next[customer.customerId] = { customerId: customer.customerId, label: `${customer.firstName || ""} ${customer.lastName || ""}`.trim() || customer.phone || customer.customerId };
            return next;
        });
    };

    const onSubmit = async () => {
        const customerIds = Object.keys(selected);
        if (!customerIds.length) { toast.error("Pick at least one customer"); return; }
        setGranting(true);
        const response = await Handler({
            method: "post",
            url: `/admin/coupon/${couponCode}/grant`,
            data: { customerIds, ...(expiresAt ? { expiresAt } : {}) },
        });
        setGranting(false);
        if (response?.success) {
            const { granted, skipped } = response?.data?.data || {};
            toast.success(`Granted to ${granted || 0} customer(s)${skipped ? `, ${skipped} already had it` : ""}`);
            onGranted?.();
        } else {
            toast.error(response?.data?.message || "Unable to grant this coupon");
        }
    };

    const selectedList = Object.values(selected);

    return (
        <div className="modal fade" tabIndex="-1" ref={modalRef} aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content border-0 rounded-4">
                    <div className="modal-header">
                        <h5 className="modal-title fw-semibold">Grant {couponCode} to customers</h5>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <div className="input-group mb-2">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by name, phone, email, or customer ID..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && runSearch()}
                            />
                            <button type="button" className="btn btn-outline-secondary" onClick={runSearch} disabled={searching}>{searching ? "..." : "Search"}</button>
                        </div>

                        {results.length > 0 ? (
                            <div className="list-group mb-3" style={{ maxHeight: 220, overflowY: "auto" }}>
                                {results.map((customer) => (
                                    <label key={customer.customerId} className="list-group-item d-flex align-items-center gap-2">
                                        <input type="checkbox" className="form-check-input m-0" checked={!!selected[customer.customerId]} onChange={() => toggleSelect(customer)} />
                                        <span>
                                            <span className="fw-semibold">{customer.firstName} {customer.lastName}</span>
                                            <span className="text-muted small ms-2">{customer.phone} · {customer.customerId}</span>
                                        </span>
                                    </label>
                                ))}
                            </div>
                        ) : ""}

                        {selectedList.length > 0 ? (
                            <div className="mb-3">
                                <div className="text-muted small mb-1">Selected ({selectedList.length})</div>
                                <div className="d-flex flex-wrap gap-1">
                                    {selectedList.map((c) => (
                                        <span key={c.customerId} className="badge text-bg-light border d-inline-flex align-items-center gap-1">
                                            {c.label}
                                            <button type="button" className="btn-close btn-close-sm" style={{ fontSize: 8 }} onClick={() => toggleSelect({ customerId: c.customerId })}></button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ) : ""}

                        <label className="form-label small text-muted mb-1">Grant expires on (optional)</label>
                        <input type="date" className="form-control" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">Cancel</button>
                        <button type="button" className="btn btn-primary" onClick={onSubmit} disabled={granting}>{granting ? "Granting..." : `Grant to ${selectedList.length || ""} customer(s)`}</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GrantModal;
